// Catalog & inventory parity overrides (gap analysis C1–C14):
// items/services/recipes editors, modifiers options, promotions, gift cards,
// stock transfers/adjustments editors, price group prices, labels, trails,
// list columns and bulk delete.
import { createElement } from 'react';

import type { CrudColumn, CrudCustomFieldProps, CrudField, CrudRecord, CrudResourceConfig, CrudRowAction } from '../types';
import { t } from '../../i18n';
import { moneyValue, names, relationName, yesNoValue } from '../configHelpers';
import { makeItemEditor } from '../../features/catalog/ItemEditor';
import { openItemLabel } from '../../features/catalog/ItemLabel';
import {
  GiftCardCustomerField,
  GiftCardNumberField,
  GiftCardPointsField,
  ModifierOptionsField,
  PromoItemField,
  PromoItemsField,
  giftCardAvailablePoints,
  translatedSelect,
  type ModifierOptionRow
} from '../../features/catalog/fieldRenderers';
import { makeStockDocEditor, normalizeAdjustmentRecordForSave, normalizeTransferRecordForSave } from '../../features/catalog/StockDocumentEditor';
import { asArray, asRecord, idsOf, makeSku, rowKey, type RemoteOption } from '../../features/catalog/shared';

type Override = (base: CrudResourceConfig | undefined) => CrudResourceConfig;

const custom = (component: (props: CrudCustomFieldProps) => unknown) => (props: CrudCustomFieldProps) => createElement(component as never, props);

// --- Item list helpers (legacy ItemListComponent renderers) ----------------

function locationStockRow(row: CrudRecord) {
  return asArray(row.location_stock)[0];
}

function num(value: unknown, decimals = 2) {
  if (value == null || value === '') return '-';
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals }) : String(value);
}

const itemCost = (row: CrudRecord) => moneyValue(locationStockRow(row)?.cost || row.cost);
const itemPrice = (row: CrudRecord) => moneyValue(locationStockRow(row)?.price || row.price);
const itemQuantity = (row: CrudRecord) => {
  const stock = locationStockRow(row);
  if (stock) return num(stock.quantity, 4);
  return num(asArray(row.stock).reduce((sum, entry) => sum + Number(entry.quantity ?? 0), 0), 4);
};
const itemRack = (row: CrudRecord) => String(locationStockRow(row)?.rack || row.rack || '-');
const extrasSummary = (row: CrudRecord) => {
  const extras = asRecord(row.extra_attributes);
  const entries = Object.entries(extras).filter(([, value]) => value != null && value !== '');
  return entries.length ? entries.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`).join(' · ') : '-';
};
const taxMethod = (row: CrudRecord) => (Number(row.tax_included) === 1 ? t('catalog.inclusive') : t('catalog.exclusive'));
const portionsSummary = (row: CrudRecord) => {
  const portions = asArray(row.portions);
  return portions.length ? portions.map((portion) => `${String(portion.name) === 'regular' ? t('catalog.regular') : String(portion.name ?? '')}: ${moneyValue(portion.price)}`).join(', ') : '-';
};
const typeLabel = (row: CrudRecord) => t(`catalog.type.${String(row.type ?? 'standard')}`);

const commonItemColumns = (extra: CrudColumn[] = []): CrudColumn[] => [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'cost', label: 'Cost', sortable: true, align: 'right', render: itemCost },
  { key: 'price', label: 'Price', sortable: true, align: 'right', render: itemPrice },
  ...extra,
  { key: 'alt_name', label: 'Alternate Name', sortable: true },
  { key: 'categories', label: 'Category', render: (row) => names(row.categories, '-') },
  { key: 'taxes', label: 'Taxes', render: (row) => names(row.taxes, '-') }
];

const tailItemColumns = (opts: { taxMethod?: boolean; flags?: boolean } = {}): CrudColumn[] => [
  { key: 'extra_attributes', label: 'Custom fields', render: extrasSummary },
  ...(opts.taxMethod ? [{ key: 'tax_included', label: 'Tax Method', sortable: true, render: taxMethod } as CrudColumn] : []),
  { key: 'changeable', label: 'Changeable Price', sortable: true, render: (row) => yesNoValue(Number(row.changeable) === 1) },
  ...(opts.flags
    ? ([
        { key: 'has_serials', label: 'Has Serials', sortable: true, render: (row) => yesNoValue(Number(row.has_serials) === 1) },
        { key: 'has_variants', label: 'Has Variants', sortable: true, render: (row) => yesNoValue(Number(row.has_variants) === 1) }
      ] as CrudColumn[])
    : []),
  { key: 'min_price', label: 'Minimum Price', sortable: true, align: 'right', render: (row) => moneyValue(row.min_price) },
  { key: 'max_price', label: 'Maximum Price', sortable: true, align: 'right', render: (row) => moneyValue(row.max_price) },
  { key: 'max_discount', label: 'Max Discount', sortable: true, align: 'right', render: (row) => num(row.max_discount) },
  { key: 'summary', label: 'Summary', sortable: true, render: (row) => { const text = String(row.summary ?? ''); return text.length > 80 ? `${text.slice(0, 80)}…` : text || '-'; } },
  { key: 'created_at', label: 'Created At', sortable: true },
  { key: 'updated_at', label: 'Updated At', sortable: true }
];

export const itemRowActions: CrudRowAction[] = [
  { key: 'label', label: 'catalog.action.printLabel', permission: 'read-items', run: (row, ctx) => openItemLabel(row, ctx) },
  { key: 'trails', label: 'catalog.action.trails', permission: 'read-items', run: (row, ctx) => ctx.navigate(`/items/${row.id}/trails`) }
];

function itemConfig(base: CrudResourceConfig | undefined, mode: 'standard' | 'service' | 'recipe', columns: CrudColumn[]): CrudResourceConfig {
  return {
    ...(base as CrudResourceConfig),
    searchMode: 'server',
    searchField: undefined,
    pageSizes: [10, 25, 50, 100],
    columns,
    // The custom editor replaces the generic form (and its broken portions /
    // variations subforms), so no generic fields are needed.
    fields: [],
    customForm: makeItemEditor(mode),
    normalizeBeforeSave: undefined,
    bulkDelete: true,
    rowActions: [...itemRowActions, ...(base?.rowActions ?? [])]
  };
}

// --- Modifiers ---------------------------------------------------------------

function modifierAfterLoad(record: CrudRecord): CrudRecord {
  const options: ModifierOptionRow[] = asArray(record.options).map((option) => {
    const item = asRecord(option.item);
    return {
      key: rowKey('opt'),
      sku: String(option.sku ?? makeSku()),
      item: option.item_id ? { value: String(option.item_id), label: item.name ? `${item.name}${item.code ? ` (${item.code})` : ''}` : String(option.item_id) } : null,
      quantity: option.quantity == null ? '' : Number(option.quantity)
    };
  });
  return { ...record, options };
}

function modifierBeforeSave(payload: Record<string, unknown>) {
  const rows = (Array.isArray(payload.options) ? payload.options : []) as ModifierOptionRow[];
  const options = rows.filter((row) => row && row.item).map((row) => ({ sku: row.sku || makeSku(), item_id: row.item!.value, quantity: row.quantity === '' ? null : Number(row.quantity) }));
  if (!options.length) throw new Error(t('catalog.modifier.optionsRequired'));
  return {
    code: payload.code,
    title: payload.title,
    show_as: payload.show_as || 'radio',
    details: payload.details ?? null,
    options
  };
}

// --- Promotions --------------------------------------------------------------

const promoTypeIs = (...types: string[]) => (form: Record<string, unknown>) => types.includes(String(form.type || 'simple'));

function itemOption(value: unknown): RemoteOption | null {
  const item = asRecord(value);
  if (!item.id) return null;
  return { value: String(item.id), label: `${String(item.name ?? '')}${item.code ? ` (${item.code})` : ''}` };
}

function promotionAfterLoad(record: CrudRecord): CrudRecord {
  return {
    ...record,
    categories: idsOf(record.categories),
    items: asArray(record.items).map((item) => itemOption(item)).filter(Boolean),
    item_to_buy: itemOption(record.item_to_buy) ?? (record.item_id_to_buy ? { value: String(record.item_id_to_buy), label: String(record.item_id_to_buy) } : null),
    item_to_get: itemOption(record.item_to_get) ?? (record.item_id_to_get ? { value: String(record.item_id_to_get), label: String(record.item_id_to_get) } : null),
    active: Number(record.active) === 1,
    start_date: record.start_date ? String(record.start_date).slice(0, 10) : '',
    end_date: record.end_date ? String(record.end_date).slice(0, 10) : ''
  };
}

const numberOrNull = (value: unknown) => (value === '' || value == null ? null : Number(value));

function promotionBeforeSave(payload: Record<string, unknown>) {
  const type = String(payload.type || 'simple');
  const items = ((Array.isArray(payload.items) ? payload.items : []) as RemoteOption[]).map((option) => option.value);
  const categories = Array.isArray(payload.categories) ? payload.categories : [];
  const toBuy = payload.item_to_buy as RemoteOption | null;
  const toGet = payload.item_to_get as RemoteOption | null;
  if (payload.start_date && payload.end_date && String(payload.end_date) < String(payload.start_date)) throw new Error(t('catalog.promo.endBeforeStart'));
  const out: Record<string, unknown> = {
    name: payload.name,
    type,
    coupon_code: payload.coupon_code || null,
    start_date: payload.start_date || null,
    end_date: payload.end_date || null,
    active: payload.active ? 1 : 0,
    details: payload.details || null,
    discount: null,
    discount_method: null,
    amount_to_spend: null,
    item_id_to_buy: null,
    quantity_to_buy: null,
    item_id_to_get: null,
    quantity_to_get: null
  };
  if (type === 'BXGY') {
    if (!toBuy || !toGet || !numberOrNull(payload.quantity_to_buy) || !numberOrNull(payload.quantity_to_get)) throw new Error(t('catalog.promo.bxgyRequired'));
    out.item_id_to_buy = toBuy.value;
    out.quantity_to_buy = numberOrNull(payload.quantity_to_buy);
    out.item_id_to_get = toGet.value;
    out.quantity_to_get = numberOrNull(payload.quantity_to_get);
    // PromotionController syncs items with items[] ?? item_id_to_buy, and the
    // request requires at least one target: send the item to buy as the target.
    out.items = [toBuy.value];
    out.categories = [];
    return out;
  }
  if (!items.length && !categories.length) throw new Error(t('catalog.promo.selectItemsOrCategories'));
  out.items = items;
  out.categories = categories;
  out.discount = numberOrNull(payload.discount);
  out.discount_method = payload.discount_method || 'percentage';
  if (type === 'advance') out.quantity_to_buy = numberOrNull(payload.quantity_to_buy);
  if (type === 'SXGD') out.amount_to_spend = numberOrNull(payload.amount_to_spend);
  return out;
}

const promoDiscount = (row: CrudRecord) => {
  if (row.discount == null || row.discount === '') return '-';
  return row.discount_method === 'percentage' ? `${num(row.discount)}%` : moneyValue(row.discount);
};

// --- Gift cards --------------------------------------------------------------

function inTwoYears() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 2);
  return date.toISOString().slice(0, 10);
}

function giftCardAfterLoad(record: CrudRecord): CrudRecord {
  const customer = asRecord(record.customer);
  return {
    ...record,
    customer: customer.id ? { value: String(customer.id), label: String(customer.name ?? customer.id) } : null,
    _original_points: Number(record.points ?? 0),
    expiry_date: record.expiry_date ? String(record.expiry_date).slice(0, 10) : ''
  };
}

function giftCardBeforeSave(payload: Record<string, unknown>) {
  const customer = payload.customer as RemoteOption | null;
  const points = customer && payload.points !== '' && payload.points != null ? Number(payload.points) : null;
  if (points != null) {
    const available = giftCardAvailablePoints(payload);
    if (available != null && points > available) throw new Error(t('catalog.giftCard.lowPoints'));
    if (points < 0) throw new Error(t('catalog.giftCard.lowPoints'));
  }
  if (!String(payload.number ?? '').trim()) throw new Error(t('catalog.validation.required', { field: t('field.cardNumber') }));
  return {
    number: String(payload.number).trim(),
    amount: Number(payload.amount ?? 0),
    expiry_date: payload.expiry_date || null,
    customer_id: customer?.value ?? null,
    points,
    details: payload.details || null
  };
}

// --- Configs -----------------------------------------------------------------

const giftCardFields: CrudField[] = [
  { name: 'number', label: 'Card Number', type: 'custom', required: true, section: 'Identity', render: custom(GiftCardNumberField) },
  { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Value' },
  { name: 'expiry_date', label: 'Expiry Date', type: 'date', defaultValue: inTwoYears(), section: 'Value' },
  { name: 'customer', label: 'Customer', type: 'custom', section: 'Customer', render: custom(GiftCardCustomerField) },
  { name: 'points', label: 'Points', type: 'custom', section: 'Customer', visibleWhen: (form) => Boolean(form.customer), render: custom(GiftCardPointsField) },
  { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
];

const promoFields: CrudField[] = [
  {
    name: 'type',
    label: 'Type',
    type: 'custom',
    required: true,
    defaultValue: 'simple',
    section: 'Identity',
    render: custom(translatedSelect([
      { value: 'simple', key: 'catalog.promo.type.simple' },
      { value: 'advance', key: 'catalog.promo.type.advance' },
      { value: 'BXGY', key: 'catalog.promo.type.BXGY' },
      { value: 'SXGD', key: 'catalog.promo.type.SXGD' }
    ]))
  },
  { name: 'name', label: 'Name', required: true, section: 'Identity' },
  { name: 'coupon_code', label: 'Coupon Code', section: 'Identity' },
  { name: 'start_date', label: 'Start Date', type: 'date', section: 'Schedule' },
  { name: 'end_date', label: 'End Date', type: 'date', section: 'Schedule' },
  { name: 'item_to_buy', label: 'Item To Buy', type: 'custom', required: true, section: 'Rules', visibleWhen: promoTypeIs('BXGY'), render: custom(PromoItemField) },
  { name: 'quantity_to_buy', label: 'Quantity To Buy', type: 'number', section: 'Rules', visibleWhen: promoTypeIs('BXGY', 'advance') },
  { name: 'item_to_get', label: 'Item To Get', type: 'custom', required: true, section: 'Rules', visibleWhen: promoTypeIs('BXGY'), render: custom(PromoItemField) },
  { name: 'quantity_to_get', label: 'Quantity To Get', type: 'number', section: 'Rules', visibleWhen: promoTypeIs('BXGY') },
  { name: 'amount_to_spend', label: 'Amount To Spend', type: 'number', section: 'Rules', visibleWhen: promoTypeIs('SXGD') },
  { name: 'items', label: 'Items', type: 'custom', section: 'Targeting', width: 'full', visibleWhen: (form) => String(form.type || 'simple') !== 'BXGY', render: custom(PromoItemsField) },
  { name: 'categories', label: 'Categories', type: 'multiselect', optionsEndpoint: 'all_categories/search', section: 'Targeting', visibleWhen: (form) => String(form.type || 'simple') !== 'BXGY' },
  { name: 'discount', label: 'Discount', type: 'number', section: 'Discount', visibleWhen: promoTypeIs('simple', 'advance', 'SXGD') },
  {
    name: 'discount_method',
    label: 'Discount Method',
    type: 'custom',
    defaultValue: 'percentage',
    section: 'Discount',
    visibleWhen: promoTypeIs('simple', 'advance', 'SXGD'),
    render: custom(translatedSelect([
      { value: 'fixed', key: 'catalog.promo.method.fixed' },
      { value: 'percentage', key: 'catalog.promo.method.percentage' }
    ]))
  },
  { name: 'active', label: 'Active', type: 'checkbox', defaultValue: true, section: 'Visibility' },
  { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
];

export const overrides: Record<string, Override> = {
  items: (base) => ({
    ...itemConfig(base, 'standard', [
      ...commonItemColumns([
        { key: 'stock', label: 'Quantity', align: 'right', render: itemQuantity },
        { key: 'rack', label: 'Rack', sortable: true, render: itemRack }
      ]),
      { key: 'type', label: 'Type', sortable: true, render: typeLabel },
      { key: 'position', label: 'Position', sortable: true },
      ...tailItemColumns({ taxMethod: true, flags: true })
    ]),
    listFilters: [
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'service', label: 'Service' },
          { value: 'recipe', label: 'Recipe' },
          { value: 'combo', label: 'Combo' }
        ]
      }
    ]
  }),
  'items-services': (base) => itemConfig(base, 'service', [...commonItemColumns(), ...tailItemColumns({ taxMethod: true })]),
  'items-recipes': (base) => itemConfig(base, 'recipe', [...commonItemColumns([{ key: 'portions', label: 'Portions', render: portionsSummary }]), ...tailItemColumns()]),
  'items-combos': (base) => ({
    ...(base as CrudResourceConfig),
    rowActions: [...itemRowActions, ...(base?.rowActions ?? [])],
    bulkDelete: true
  }),

  modifiers: (base) => ({
    ...(base as CrudResourceConfig),
    searchMode: 'server',
    searchField: undefined,
    bulkDelete: true,
    customFields: true,
    columns: [
      { key: 'code', label: 'Code', sortable: true },
      { key: 'title', label: 'Title', sortable: true },
      { key: 'show_as', label: 'Show As', sortable: true, render: (row) => t(`catalog.modifier.showAs.${String(row.show_as ?? 'radio')}`) },
      { key: 'options', label: 'Options', render: (row) => asArray(row.options).map((option) => `${String(asRecord(option.item).name ?? option.item_id ?? '')}${option.quantity ? ` × ${num(option.quantity)}` : ''}`).join(', ') || '-' },
      { key: 'details', label: 'Details' }
    ],
    detailSections: [
      { title: 'Identity', fields: [{ key: 'code', label: 'Code' }, { key: 'title', label: 'Title' }, { key: 'show_as', label: 'Show As', render: (row) => t(`catalog.modifier.showAs.${String(row.show_as ?? 'radio')}`) }] },
      { title: 'Options', render: (row) => asArray(row.options).map((option) => `${String(asRecord(option.item).name ?? option.item_id ?? '')} × ${num(option.quantity ?? 1)}`).join('\n') || '-' },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'title', label: 'Title', required: true, section: 'Identity' },
      {
        name: 'show_as',
        label: 'Show As',
        type: 'custom',
        required: true,
        defaultValue: 'radio',
        section: 'Identity',
        render: custom(translatedSelect(['radio', 'checkbox', 'select', 'select_multiple'].map((value) => ({ value, key: `catalog.modifier.showAs.${value}` }))))
      },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Identity' },
      { name: 'options', label: 'Options', type: 'custom', section: 'Options', width: 'full', defaultValue: [], render: custom(ModifierOptionsField) }
    ],
    normalizeAfterLoad: modifierAfterLoad,
    normalizeBeforeSave: modifierBeforeSave
  }),

  promos: (base) => ({
    ...(base as CrudResourceConfig),
    searchMode: 'server',
    // Promotion::$searchable lists a non-existent location_id column, so a
    // free-text search 500s: search by name like the legacy list.
    searchField: 'name',
    bulkDelete: true,
    columns: [
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'type', label: 'Type', sortable: true, render: (row) => t(`catalog.promo.type.${String(row.type ?? 'simple')}`) },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'coupon_code', label: 'Coupon Code', sortable: true },
      { key: 'active', label: 'Active', sortable: true, render: (row) => yesNoValue(Number(row.active) === 1) },
      { key: 'start_date', label: 'Start Date', sortable: true },
      { key: 'end_date', label: 'End Date', sortable: true },
      { key: 'discount', label: 'Discount', sortable: true, align: 'right', render: promoDiscount },
      { key: 'item_to_buy', label: 'Item To Buy', render: (row) => relationName(row.item_to_buy) },
      { key: 'quantity_to_buy', label: 'Quantity To Buy', sortable: true, render: (row) => num(row.quantity_to_buy) },
      { key: 'item_to_get', label: 'Item To Get', render: (row) => relationName(row.item_to_get) },
      { key: 'quantity_to_get', label: 'Quantity To Get', sortable: true, render: (row) => num(row.quantity_to_get) },
      { key: 'details', label: 'Details' }
    ],
    rowTone: (row) => (Number(row.active) === 1 ? undefined : 'warning'),
    fields: promoFields,
    normalizeAfterLoad: promotionAfterLoad,
    normalizeBeforeSave: promotionBeforeSave
  }),

  gift_cards: (base) => ({
    ...(base as CrudResourceConfig),
    searchMode: 'server',
    searchField: undefined,
    bulkDelete: true,
    columns: [
      { key: 'number', label: 'Card Number', sortable: true },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer) },
      { key: 'amount', label: 'Amount', sortable: true, align: 'right', render: (row) => moneyValue(row.amount) },
      { key: 'balance', label: 'Balance', sortable: true, align: 'right', render: (row) => moneyValue(row.balance) },
      { key: 'points', label: 'Points', sortable: true, render: (row) => num(row.points) },
      { key: 'expiry_date', label: 'Expiry Date', sortable: true },
      { key: 'created_at', label: 'Created At', sortable: true }
    ],
    fields: giftCardFields,
    normalizeAfterLoad: giftCardAfterLoad,
    normalizeBeforeSave: giftCardBeforeSave,
    rowActions: [
      { key: 'logs', label: 'catalog.action.giftCardLogs', permission: 'read-gift-cards', run: (row, ctx) => ctx.navigate(`/gift-cards/${row.id}/logs`) },
      ...(base?.rowActions ?? [])
    ]
  }),

  price_groups: (base) => ({
    ...(base as CrudResourceConfig),
    bulkDelete: true,
    rowActions: [
      { key: 'prices', label: 'catalog.action.prices', permission: 'update-price-groups', run: (row, ctx) => ctx.navigate(`/price-groups/${row.id}/prices`) },
      ...(base?.rowActions ?? [])
    ]
  }),

  'transfers/stock': (base) => ({
    ...(base as CrudResourceConfig),
    bulkDelete: true,
    showAttachments: true,
    customForm: makeStockDocEditor('transfer'),
    // Used by the Finalize action (status pending → transferred).
    normalizeBeforeSave: normalizeTransferRecordForSave,
    columns: [
      { key: 'date', label: 'Date', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'status', label: 'Status', sortable: true, render: (row) => t(`catalog.transfer.status.${String(row.status ?? 'pending')}`) },
      { key: 'from', label: 'From', render: (row) => relationName(row.from_location ?? row.fromLocation) },
      { key: 'to', label: 'To', render: (row) => relationName(row.to_location ?? row.toLocation) },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) },
      { key: 'created_at', label: 'Created At', sortable: true }
    ]
  }),

  'transfers/asset': (base) => ({
    ...(base as CrudResourceConfig),
    bulkDelete: true
  }),

  adjustments: (base) => ({
    ...(base as CrudResourceConfig),
    requiresLocation: true,
    customFields: true,
    showAttachments: true,
    customForm: makeStockDocEditor('adjustment'),
    // Used by the Finalize action (draft → posted).
    normalizeBeforeSave: normalizeAdjustmentRecordForSave
  })
};
